import { callable, CallableError } from '../backend/callables';
import type { VaultSession } from '../session/vaultSession';
import { clearPersistedAccount } from '../session/authSession';
import { clearAll } from './cache';
import type { GuardChoice, GuardKind, RememberOutcome, UnlockOutcome } from './trustedBrowser';
import {
  forgetThisBrowser,
  hasRememberedBrowser,
  openRememberedBrowser,
  rememberedGuard,
  rememberThisBrowser,
} from './trustedBrowser';

/**
 * The three backend calls behind a remembered browser, and the one distinction
 * that matters when wiring them: a REFUSAL is final and a FAILURE is not.
 *
 * `permission-denied` means the grant is gone — revoked, expired, another
 * account — and the share has already been destroyed server-side, so what is
 * stored here can never open again and is dropped. Anything else (offline, a
 * bad minute) has to leave the stored blob alone, or a flaky connection would
 * cost the user a QR and a phone.
 */

export { hasRememberedBrowser, rememberedGuard };

export async function rememberBrowser(
  idToken: string,
  session: VaultSession,
  accountLabel: string,
  choice: GuardChoice,
): Promise<RememberOutcome> {
  return rememberThisBrowser(session, accountLabel, choice, async () => {
    try {
      // The guard rides along so the phone's list can say what protects this
      // browser. Asserted by the client, which is all it can be — the backend
      // cannot witness a fingerprint — and it grants nothing, so a client that
      // lied about it would gain only a wrong word in its own session list.
      return await callable<{ trustId: string; share: string }>(
        'mintVaultTrust',
        { guard: choice.kind },
        { idToken, grantSecret: session.grantSecret },
      );
    } catch (error) {
      // Refused is the ordinary answer when the phone approved a session-only
      // pass; it is a decision, not a fault, so it must not read as one.
      if (error instanceof CallableError && error.needsRepair) return null;
      throw error;
    }
  });
}

export async function unlockBrowser(idToken: string, phrase?: string): Promise<UnlockOutcome> {
  return openRememberedBrowser(
    async (trustId) => {
      try {
        const { share } = await callable<{ share: string }>('getVaultTrust', { trustId }, { idToken });
        return share;
      } catch (error) {
        if (error instanceof CallableError && error.needsRepair) return null;
        throw error;
      }
    },
    undefined,
    phrase,
  );
}

/** Local first, then the server, then the cache: a browser told to forget must
 *  forget even if none of the calls land. */
export async function forgetBrowser(idToken: string | null): Promise<void> {
  const trustId = await forgetThisBrowser();
  // Before the network, for the same reason the blob goes first: the stored
  // account token was granted alongside the trust record and must not outlive
  // it, whatever the calls below do.
  clearPersistedAccount();
  if (trustId && idToken) {
    await callable('forgetVaultTrust', { trustId }, { idToken }).catch(() => {});
  }
  await clearAll();
}

/** What the surfaces call this guard, in one place so they agree. */
export const guardLabel = (guard: GuardKind): string =>
  guard === 'hardware' ? 'this device' : guard === 'passphrase' ? 'an unlock phrase' : 'nothing asked';
